/**
 * Collection executor — runs collection tasks with concurrency control.
 *
 * Executes tasks built by the task-builder, routing each to the appropriate
 * collector (GDELT, RSS, API). Manages:
 * - Configurable concurrency (default 3 parallel)
 * - Per-task timeout + 1 retry on transient failure
 * - Results stored as raw_data on collection task in SQLite
 * - Emits investigation:task-completed per task
 * - Collection logging of every action
 *
 * ARCHITECTURE.md section 5.4.5: The orchestrator constructs queries,
 * the executor fetches data. LLM is not involved.
 */

import { eventBus } from '@/core/events';
import type { CollectionTask, CollectionTaskStatus } from './types';
import {
  getCollectionTasks,
  updateCollectionTask,
  addLogEntry,
} from './investigation-store';
import { isDemoMode } from '@/intelligence/ai/provider-registry';
import { collectFromGDELT } from './collectors/gdelt-collector';
import { collectFromRSS } from './collectors/rss-collector';
import { collectFromAPI } from './collectors/api-collector';
import { collectFromShodan } from './collectors/shodan-collector';
import { collectFromBGPView } from './collectors/bgpview-collector';
import { collectFromWHOIS } from './collectors/whois-collector';
import { collectFromDNS } from './collectors/dns-collector';
import { collectFromVirusTotal } from './collectors/virustotal-collector';
import { collectFromCuratedWeb } from './collectors/curated-web-collector';
import { collectFromWebSearch } from './collectors/web-search-collector';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** Default maximum concurrent collection tasks. */
const DEFAULT_CONCURRENCY = 3;

/** Default per-task timeout in ms (30 seconds). */
const DEFAULT_TASK_TIMEOUT_MS = 30_000;

/** Number of retries on transient failure. */
const MAX_RETRIES = 1;

/** HTTP status codes considered transient (worth retrying). */
const TRANSIENT_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CollectionExecutorConfig {
  /** Maximum concurrent tasks (default: 3). */
  concurrency?: number;
  /** Per-task timeout in ms (default: 30000). */
  taskTimeoutMs?: number;
}

export interface CollectionExecutionResult {
  investigationId: string;
  totalTasks: number;
  completed: number;
  failed: number;
  timedOut: number;
}

// ---------------------------------------------------------------------------
// Demo mode — mock collection results (zero API calls)
// ---------------------------------------------------------------------------

/**
 * Simulated collection results for the Baltic maritime scenario.
 * Each entry represents what a real source would return — realistic
 * enough for the extraction pipeline to produce meaningful entities.
 *
 * Only used when demo mode is active. The real pipeline is untouched.
 */
const DEMO_COLLECTION_TEXTS: Record<string, string> = {
  gdelt: [
    'HEADLINE: Russian frigate Admiral Gorshkov conducts exercises south of Gotland',
    'SOURCE: Reuters, 2026-03-25',
    'Russian Navy frigate Admiral Gorshkov conducted exercises south of Gotland in the central Baltic Sea on Tuesday, according to the Swedish Armed Forces. The Northern Fleet vessel, a Project 22350 frigate, was observed by Swedish maritime patrol aircraft operating approximately 30 nautical miles south of the island.',
    '',
    'The Swedish Armed Forces raised readiness level for naval units near Gotland in response. "We are closely monitoring the situation," a spokesperson said.',
    '',
    'Admiral Nikolai Yevmenov, Commander-in-Chief of the Russian Navy, authorized the deployment of additional surface combatants to the Baltic region last week, Russian defense ministry sources confirmed.',
    '',
    '---',
    '',
    'HEADLINE: Shadow fleet tanker spotted transiting Danish Straits with intermittent AIS',
    'SOURCE: Lloyd\'s List, 2026-03-24',
    'The NS Concord, a Russian-flagged crude oil tanker suspected of shadow fleet operations, was observed transiting the Danish Straits with its AIS transponder intermittently active. Maritime analysts noted a characteristic shadow fleet pattern of 45-minute AIS gaps followed by brief transmissions.',
    '',
    'The vessel was subsequently observed loitering near the Nord Stream pipeline route in the central Baltic, raising concerns about potential surveillance activity. The NS Concord (IMO 9305027, MMSI 273459120) departed from Baltiysk naval base in Kaliningrad earlier this week.',
    '',
    '---',
    '',
    'HEADLINE: Multiple vessels lose AIS contact simultaneously in central Baltic',
    'SOURCE: MarineTraffic Intelligence, 2026-03-23',
    'Multiple vessels simultaneously lost AIS contact in the central Baltic, consistent with electronic warfare activity, according to maritime tracking services. The disruption affected at least 4 vessels over a 6-hour period near Gotland.',
    '',
    'Baltic states have previously reported GPS and GNSS jamming in the region, with Finland and Estonia attributing the interference to Russian military electronic warfare systems based in Kaliningrad Oblast.',
  ].join('\n'),

  rss: [
    'HEADLINE: Sweden bolsters Gotland defenses amid increased Russian naval presence',
    'SOURCE: SVT Nyheter, 2026-03-26',
    'The Swedish Armed Forces (Försvarsmakten) have deployed additional naval assets to waters near Gotland following reports of heightened Russian military activity in the Baltic Sea. Defence Minister confirmed that readiness levels were raised as a precautionary measure.',
    '',
    '---',
    '',
    'HEADLINE: Baltic Fleet activity increases from Baltiysk base',
    'SOURCE: Janes Defence, 2026-03-25',
    'Satellite imagery confirms increased activity at Baltiysk Naval Base in Kaliningrad, the main operating base of the Russian Baltic Fleet. Multiple surface combatants were observed departing the port, including at least one frigate-class vessel.',
  ].join('\n'),

  api: [
    'AIS DATA SUMMARY — Baltic Region, Past 7 Days',
    '',
    'Vessel: NS Concord | IMO: 9305027 | MMSI: 273459120 | Flag: Russia | Type: Crude Oil Tanker',
    'Position: 55.8°N, 16.2°E (central Baltic) | Speed: 3.2 kn | Course: 045°',
    'AIS gaps detected: 7 events, avg duration 42 minutes, pattern consistent with intentional cycling',
    '',
    'Vessel: Admiral Gorshkov | Hull: 454 | Flag: Russia | Type: Frigate (Project 22350)',
    'Position: 57.1°N, 18.3°E (south of Gotland) | Speed: 14.5 kn | Course: 270°',
    'Classification: Military — Northern Fleet surface combatant operating outside home waters',
    '',
    'Area alert: AIS disruption zone detected 56.5-57.5°N, 17.0-19.0°E. Affected vessels: 4. Duration: 6 hours.',
  ].join('\n'),

  telegram: [
    'Channel: @balticosint — Baltic OSINT',
    '',
    '[2026-03-25 14:32 UTC] Russian Navy frigate spotted south of Gotland by Swedish coast guard. Photos confirm Admiral Gorshkov class.',
    '',
    '[2026-03-24 09:15 UTC] Shadow fleet tanker NS Concord passed through Danish Straits heading east. AIS going dark every ~45 min.',
    '',
    '[2026-03-23 22:00 UTC] Reports of AIS blackout zone forming near Gotland. Likely EW activity from Kaliningrad direction.',
  ].join('\n'),
};

/** Default demo text for sources without a specific mock. */
const DEMO_DEFAULT_TEXT = 'No additional data available from this source for the current investigation scope.';

/** Min/max simulated collection latency (ms). */
const DEMO_MIN_LATENCY = 200;
const DEMO_MAX_LATENCY = 800;

// ---------------------------------------------------------------------------
// Task router
// ---------------------------------------------------------------------------

/**
 * Route a collection task to the appropriate collector based on its type.
 * Returns the raw text data collected.
 */
async function executeTask(
  task: CollectionTask,
  timeoutMs: number,
): Promise<string> {
  // Demo mode: return mock collection data without any API calls.
  // The extraction pipeline still runs normally on this text.
  if (isDemoMode()) {
    const delay = DEMO_MIN_LATENCY + Math.random() * (DEMO_MAX_LATENCY - DEMO_MIN_LATENCY);
    await new Promise(resolve => setTimeout(resolve, delay));
    const queryType = (task.query as Record<string, unknown>).type as string | undefined;
    return DEMO_COLLECTION_TEXTS[queryType ?? ''] ?? DEMO_DEFAULT_TEXT;
  }

  const queryType = (task.query as Record<string, unknown>).type as string | undefined;

  switch (queryType) {
    case 'gdelt': {
      const result = await collectFromGDELT(task, timeoutMs);
      return result.rawText;
    }
    case 'rss': {
      const result = await collectFromRSS(task, timeoutMs);
      return result.rawText;
    }
    case 'api': {
      const result = await collectFromAPI(task, timeoutMs);
      return result.rawText;
    }
    case 'telegram': {
      // Telegram collector is a stub for now — returns empty
      // Full implementation in a later session when Telegram API is integrated
      return 'Telegram collection not yet implemented.';
    }
    case 'shodan': {
      const result = await collectFromShodan(task, timeoutMs);
      return result.rawText;
    }
    case 'bgpview': {
      const result = await collectFromBGPView(task, timeoutMs);
      return result.rawText;
    }
    case 'whois': {
      const result = await collectFromWHOIS(task, timeoutMs);
      return result.rawText;
    }
    case 'dns': {
      const result = await collectFromDNS(task, timeoutMs);
      return result.rawText;
    }
    case 'virustotal': {
      const result = await collectFromVirusTotal(task, timeoutMs);
      return result.rawText;
    }
    case 'curated_web': {
      const query = task.query as Record<string, unknown>;
      const webSources = (query.webSources ?? []) as Array<{ url: string; name: string; reliability: 'A' | 'B'; scrapePattern?: string }>;
      const result = await collectFromCuratedWeb(task, webSources);
      return result.rawText;
    }
    case 'web_search': {
      const query = task.query as Record<string, unknown>;
      const searxngUrl = (query.searxngUrl as string) ?? undefined;
      const maxResults = (query.maxResults as number) ?? 10;
      const result = await collectFromWebSearch(task, searxngUrl, maxResults);
      return result.rawText;
    }
    default: {
      // Try API collector as generic fallback
      const result = await collectFromAPI(task, timeoutMs);
      return result.rawText;
    }
  }
}

/**
 * Determine if an error is transient (worth retrying).
 */
function isTransientError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    // Network errors
    if (msg.includes('timeout') || msg.includes('abort') || msg.includes('econnreset')) {
      return true;
    }
    // HTTP status codes in error messages
    for (const code of TRANSIENT_STATUS_CODES) {
      if (msg.includes(String(code))) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Concurrency pool
// ---------------------------------------------------------------------------

/**
 * Run async functions with bounded concurrency.
 * Uses a worker-pool pattern: spawns `limit` workers, each pulls
 * sequentially from a shared index counter (no race on shift).
 */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const idx = cursor++;
      await fn(items[idx]);
    }
  }

  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(limit, items.length); i++) {
    workers.push(worker());
  }

  await Promise.all(workers);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Execute all pending collection tasks for an investigation.
 *
 * Runs tasks in parallel up to the concurrency limit.
 * Each task: fetch → store raw_data → emit event → log.
 * On failure: 1 retry for transient errors, then mark failed.
 */
export async function executeCollectionTasks(
  investigationId: string,
  config?: CollectionExecutorConfig,
): Promise<CollectionExecutionResult> {
  const concurrency = config?.concurrency ?? DEFAULT_CONCURRENCY;
  const taskTimeoutMs = config?.taskTimeoutMs ?? DEFAULT_TASK_TIMEOUT_MS;

  // Load all pending tasks for this investigation
  const allTasks = await getCollectionTasks(investigationId);
  const pendingTasks = allTasks.filter(t => t.status === 'pending');

  const result: CollectionExecutionResult = {
    investigationId,
    totalTasks: pendingTasks.length,
    completed: 0,
    failed: 0,
    timedOut: 0,
  };

  if (pendingTasks.length === 0) {
    return result;
  }

  // Execute tasks with concurrency control
  await runWithConcurrency(pendingTasks, concurrency, async (task) => {
    await executeSingleTask(task, taskTimeoutMs, investigationId, result);
  });

  return result;
}

/**
 * Execute a single collection task with retry logic.
 */
async function executeSingleTask(
  task: CollectionTask,
  timeoutMs: number,
  investigationId: string,
  result: CollectionExecutionResult,
): Promise<void> {
  const startedAt = new Date().toISOString();

  // Mark as running
  await updateCollectionTask(task.id, {
    status: 'running',
    startedAt,
  });

  let lastError: string | null = null;
  let rawData: string | null = null;
  let finalStatus: CollectionTaskStatus = 'failed';

  // Try up to MAX_RETRIES + 1 times
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      rawData = await executeTask(task, timeoutMs);
      finalStatus = 'completed';
      break;
    } catch (err) {
      lastError = err instanceof Error ? err.message : String(err);

      // Check if the error indicates a timeout
      if (lastError.toLowerCase().includes('timeout') || lastError.toLowerCase().includes('abort')) {
        finalStatus = 'timeout';
      }

      // Retry only on transient errors
      if (attempt < MAX_RETRIES && isTransientError(err)) {
        await addLogEntry({
          investigationId,
          state: 'collect',
          action: 'task_failed',
          detail: {
            taskId: task.id,
            sourceId: task.sourceId,
            attempt: attempt + 1,
            error: lastError,
            retrying: true,
          },
        });
        // Brief delay before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      // Non-transient error or out of retries
      break;
    }
  }

  const completedAt = new Date().toISOString();

  // Update task in SQLite
  await updateCollectionTask(task.id, {
    status: finalStatus,
    rawData,
    completedAt,
    error: finalStatus !== 'completed' ? lastError : null,
  });

  // Update result counters
  switch (finalStatus) {
    case 'completed':
      result.completed++;
      break;
    case 'timeout':
      result.timedOut++;
      break;
    default:
      result.failed++;
      break;
  }

  // Log the task completion/failure
  const logAction = finalStatus === 'completed' ? 'task_completed' as const : 'task_failed' as const;
  await addLogEntry({
    investigationId,
    state: 'collect',
    action: logAction,
    detail: {
      taskId: task.id,
      sourceId: task.sourceId,
      status: finalStatus,
      rawDataLength: rawData?.length ?? 0,
      error: lastError,
      startedAt,
      completedAt,
    },
  });

  // Emit event for the task
  const updatedTask: CollectionTask = {
    ...task,
    status: finalStatus,
    rawData,
    startedAt,
    completedAt,
    error: finalStatus !== 'completed' ? lastError : null,
  };

  eventBus.emit('investigation:task-completed', {
    investigationId,
    task: updatedTask,
  });
}
